import { z } from 'zod'
import { Sdk } from '../../__generated__/sdk.generated'
import { getPixelClient } from '../../analytics'
import { logger } from '../../util/logger'
import { withFormattedErrors } from '../input-validation'
import { useListingQueries } from '../trigger/listing-queries'
import { mountListingTrigger } from '../trigger/listing-trigger'
import { useMountReferredTrigger } from '../trigger/referred-trigger'
import { OfferKitEmbeddable } from './offerkit'

export function useMount({
  kit,
  sdk,
  mountReferredTrigger = useMountReferredTrigger(),
}: {
  kit: OfferKitEmbeddable
  sdk: Sdk
  mountReferredTrigger?: ReturnType<typeof useMountReferredTrigger>
}) {
  const queries = useListingQueries(sdk)

  const mount = z
    .function()
    .args(
      z.object({
        type: z.enum(['native', 'referred']).default('native'),
        listingId: z.string(),
        isInternal: z.boolean().optional(),
        customMetadata: z.string().optional(),
        forceMount: z.boolean().optional(),
        openOnMount: z.boolean().optional(),
        selector: z.string().optional(),
        container: z.union([z.string(), z.instanceof(HTMLElement)]).optional(),
        populate: z.any().optional(),
        overrideLabel: z.string().optional(),
      })
    )
    .implement(async (input) => {
      const container =
        typeof input.container === 'string'
          ? document.querySelector<HTMLElement>(input.container)
          : input.container ?? null
      const target = input.selector
        ? document.querySelector<HTMLElement>(input.selector)
        : null

      if (typeof input.selector === 'string' && !target) {
        logger.error(
          'Cannot mount, no element with the provided selector was found: ' +
            input.selector
        )
        return null
      }

      if (input.type === 'referred') {
        const trigger = await mountReferredTrigger({
          source: { appId: kit.appId!, foreignId: input.listingId },
          populate: input.populate,
          container,
          target,
          openOnMount: input.openOnMount,
          customMetadata: input.customMetadata,
          defaultButtonHost: kit.triggerHost!,
          framesChannel: kit.channel,
          overrideLabel: input.overrideLabel,
          pixel: getPixelClient(),
        })
        kit.triggers.add(trigger)
        return trigger
      }

      const listing =
        kit.internalMode || input.isInternal
          ? await queries.fetchListingById(input.listingId)
          : await queries.fetchListingByForeignId({
              appId: kit.appId!,
              foreignId: input.listingId,
            })

      if (!listing) {
        logger.error('Cannot mount, listing not found:', input.listingId)
        return null
      }

      const trigger = await mountListingTrigger({
        listing,
        container,
        target,
        openOnMount: input.openOnMount,
        customMetadata: input.customMetadata,
        defaultButtonHost: kit.triggerHost!,
        framesChannel: kit.channel,
        internalMode: kit.internalMode,
        overrideLabel: input.overrideLabel,
        pixel: getPixelClient(),
      })

      if (!trigger) {
        logger.warn('Listing found but did not mount.')
        return null
      }

      kit.triggers.add(trigger)
      logger.log('Mounted trigger for', listing.id)

      return trigger
    })

  return withFormattedErrors(mount)
}

export type Mount = ReturnType<typeof useMount>
